/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import DataActionBodyAttributeRows from 'cio/app-models/data-action/body-attribute-rows';
import DataActionRecipientCondition from 'cio/app-models/data-action/recipient-condition';
import * as JSONBig from 'json-bigint';
import {
  DataActionAutocompleteValuesContainer,
  DataActionConditionType,
  DataActionTriggerAttributeDropdownItem,
} from 'cio/utils/campaigns/data-action';
import { CreateEventPreview } from 'cio/utils/previews';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { IdentifierKey } from 'cio/utils/identifiers';
import { CREATED_IN_CIO_AT } from 'cio/utils/attributes';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';

interface CreateOrUpdatePersonEditorAttributesArgs {
  editableEntity: ComposerEditableEntity;
  title: string;
  recipientCondition?: DataActionRecipientCondition;
  preview: CreateEventPreview;
  isPreviewLoading: boolean;
  trackPreviewErrors: (
    element: HTMLElement,
    [errors]: [{ field: string; label: string; errors: string[] }[]],
  ) => unknown;
  autocompleteValues: DataActionAutocompleteValuesContainer;
  isLoadingAsyncAutocompleteValues: boolean;
  supportedConditionTypes: DataActionConditionType[];
  attributeUpdates: DataActionBodyAttributeRows;
  hasReadOnlyPermission: boolean;
  updateHighlight: (highlight: DataActionTriggerAttributeDropdownItem | null) => unknown;
  previewDidChange?: () => unknown;
  liquidFieldFocusIn: (fieldName: string, index?: number) => unknown;
  liquidFieldFocusOut: () => unknown;
}

export default class CreateOrUpdatePersonEditorAttributes extends Component<CreateOrUpdatePersonEditorAttributesArgs> {
  @service environment!: EnvironmentService;

  get showOptionalLabel() {
    if (this.args.recipientCondition?.mustMatchRecipient ?? false) {
      return true;
    }

    return this.args.recipientCondition?.shouldMatchRecipient;
  }

  get showLiquidTypeFallbackNotice() {
    if (!(this.args.recipientCondition?.mustMatchRecipient ?? false)) {
      return this.args.recipientCondition?.shouldMatchRecipient;
    }

    return true;
  }

  get attributeNameAutocompleteValues() {
    return this.environment.sentAttributes.filter((attr) => ![IdentifierKey.CioId, CREATED_IN_CIO_AT].includes(attr));
  }

  get previewString() {
    let data: Record<string | number, string | number | boolean | null> = {};

    try {
      let parsedData = JSONBig.parse(this.args.preview?.body);
      // Make sure the preview object has keys in the same order as the rows
      this.args.attributeUpdates.nonEmptyMappedRows.forEach((row) => {
        data[row.name] = parsedData[row.name] ?? '';
      });
    } catch {}

    return JSONBig.stringify(data, null, '  ');
  }
}
